import Pane from 'components/Common/Pane'
import TextLink from 'components/Luxkit/TextLink'
import Heading from 'components/Luxkit/Typography/Heading'
import Group from 'components/utils/Group'
import { groupBy, sortBy } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import TourV2DepartureTile from './TourV2DepartureTile'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { INTL_LONG_MONTH } from 'constants/dateFormats'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const GroupTitle = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.default.six};
  padding: ${rem(12)} ${rem(20)};
`

function getGroupKey(date: Date) {
  return `${date.getFullYear()}-${date.getMonth()}`
}

interface Props {
  offer: Tours.TourV2Offer;
  departures: Array<Tours.TourV2OfferDeparture>;
  variation: Tours.TourV2OfferVariation;
  onDepartureSelect?: (departure: Tours.TourV2OfferDeparture) => void;
  onYearSelect?: (year: number) => void;
  defaultDate?: Date;
  travellers?: Array<App.Occupants>;
}

function TourV2DepartureTileList(props: Props) {
  const { departures, offer, onDepartureSelect, onYearSelect, defaultDate, travellers, variation } = props

  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const dateFormatter = useIntlDateFormatter()

  const departureGroups = useMemo(() => {
    const departuresByMonth = groupBy(departures, dep => {
      const date = new Date(dep.startDate)
      return getGroupKey(date)
    })

    return departuresByMonth.entries().map(([key, departures]) => {
      const date = new Date(departures[0].startDate)
      return {
        key,
        month: dateFormatter(date, INTL_LONG_MONTH),
        year: date.getFullYear(),
        departures: sortBy(departures, dep => new Date(dep.startDate), 'asc'),
      }
    }).toArray()
  }, [dateFormatter, departures])

  const onYearClick = useCallback<React.MouseEventHandler<HTMLElement>>((e) => {
    const year = parseInt(e.currentTarget.dataset.year!)
    onYearSelect?.(year)
  }, [onYearSelect])

  useLayoutEffect(() => {
    if (defaultDate) {
      const element = groupRefs.current[getGroupKey(defaultDate)]
      if (element) {
        element.scrollIntoView({ behavior: 'instant', block: 'start' })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <Pane type="light">
    {departureGroups.map(group => <div
      key={group.key}
      ref={ref => groupRefs.current[group.key] = ref}
    >
      <GroupTitle direction="horizontal" horizontalAlign="space-between" gap={20}>
        <Heading variant="heading5">{group.month}</Heading>
        <TextLink size="large" weight="bold" onClick={onYearClick} data-year={group.year}>
          {group.year}
        </TextLink>
      </GroupTitle>
      <VerticalSpacer gap={8}>
        {group.departures.map(departure => {
          return <TourV2DepartureTile
            key={departure.id}
            departure={departure}
            offer={offer}
            onSelect={onDepartureSelect}
            travellers={travellers}
            variation={variation}
          />
        })}
      </VerticalSpacer>
    </div>)}
  </Pane>
}

export default React.memo(TourV2DepartureTileList)
